import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";

interface ChartPoint {
  month: string;
  value: number | string;
}

interface RevenueCommissionsProps {
  title: string;
  subtitle?: string;
  data: ChartPoint[];
  total?: number | string;
  color?: string;
  valuePrefix?: string;
  valueSuffix?: string;
  gradientId: string; // unique per instance to avoid SVG id collision
}

function ChartTooltip({ active, payload, valuePrefix, valueSuffix }: any) {
  if (!active || !payload?.length) return null;
  const val = payload.find((p: any) => p.dataKey === "value")?.value ?? 0;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-lg p-3 text-xs min-w-[140px]">
      <div className="flex justify-between font-semibold text-gray-900">
        <span>{payload[0]?.payload?.month}</span>
        <span>{valuePrefix}{Number(val).toLocaleString()}{valueSuffix}</span>
      </div>
    </div>
  );
}

// Format "2026-10" → "Oct"
function fmtMonth(m: string) {
  const [, mm] = m.split("-");
  return new Date(2000, Number(mm) - 1).toLocaleString("en", { month: "short" });
}

export function RevenueCommissions({
  title,
  subtitle,
  data,
  total,
  color = "#6366f1",
  valuePrefix = "",
  valueSuffix = "",
  gradientId,
}: RevenueCommissionsProps) {
  const { t } = useTranslation();
  const chartData = data.map((d) => ({
    month: fmtMonth(d.month),
    value: Number(d.value),
  }));

  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
          {subtitle && (
            <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
          )}
        </div>
        {total !== undefined && (
          <div className="text-right shrink-0">
            <p
              className="text-2xl font-bold leading-none"
              style={{ color }}
            >
              {valuePrefix}
              {Number(total).toLocaleString()}
              {valueSuffix}
            </p>
            <p className="text-[10px] text-gray-400 mt-1">
              {t("affiliate.total")}
            </p>
          </div>
        )}
      </div>

      <div className="h-[160px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 6, right: 4, left: -16, bottom: 0 }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={color} stopOpacity={0.15} />
                <stop offset="100%" stopColor={color} stopOpacity={0}    />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `${valuePrefix}${v}${valueSuffix}`}
              width={40}
            />
            <Tooltip
              content={
                <ChartTooltip
                  valuePrefix={valuePrefix}
                  valueSuffix={valueSuffix}
                />
              }
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 4, fill: color }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
